(function () {
  'use strict';

  var STORAGE_KEY = 'nh:ablyft:cart-stage:v1';
  var BASELINE_STAGE = 'stage-1';
  var VIEWS = {
    'stage-2': 'ablyft-stage-2',
    'stage-3': 'ablyft-stage-3',
    'stage-4': 'ablyft-stage-4',
  };

  var QA_STORAGE_KEY = 'nh:qa:cart-stage';

  // QA without ablyft: ?nh_ab=cart-stage-1 … cart-stage-4, reset with ?nh_ab=cart-control.
  // The choice is kept for the browser tab and wins over the ablyft assignment.
  function readQaStage() {
    try {
      var param = new URLSearchParams(window.location.search).get('nh_ab');

      if (param === 'cart-control') {
        window.sessionStorage.removeItem(QA_STORAGE_KEY);
        return 'control';
      }

      var match = param && param.match(/^cart-(stage-[1-4])$/);
      if (match) {
        window.sessionStorage.setItem(QA_STORAGE_KEY, match[1]);
        return match[1];
      }

      var storedStage = window.sessionStorage.getItem(QA_STORAGE_KEY);
      return VIEWS[storedStage] ? storedStage : null;
    } catch (error) {
      return null;
    }
  }

  function readStage() {
    var qaStage = readQaStage();
    if (qaStage) return qaStage;

    try {
      var storedValue = window.localStorage.getItem(STORAGE_KEY);
      if (!storedValue) return 'control';

      if (VIEWS[storedValue]) return storedValue;
      if (storedValue === 'control') return 'control';

      var parsedValue = JSON.parse(storedValue);
      if (!parsedValue || !VIEWS[parsedValue.stage]) return 'control';
      if (parsedValue.expiresAt && Date.now() > Number(parsedValue.expiresAt)) {
        window.localStorage.removeItem(STORAGE_KEY);
        return 'control';
      }

      return parsedValue.stage;
    } catch (error) {
      console.warn('Cart experiment assignment could not be read; using control.', error);
      return 'control';
    }
  }

  function bindExperimentRemove() {
    if (window.nhCartItemRemoveBound) return;
    window.nhCartItemRemoveBound = true;

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-nh-cart-remove]');
      if (!trigger) return;

      event.preventDefault();
      var key = trigger.getAttribute('data-id');
      var form = trigger.closest('form[data-location]');
      if (!key || !form) return;

      var confirmationMessage = trigger.getAttribute('data-confirm-message');
      if (confirmationMessage && !window.confirm(confirmationMessage)) return;

      document.dispatchEvent(new CustomEvent('cart:quantity.cart-' + form.getAttribute('data-location'), {
        detail: [key, 0, trigger],
      }));
    });
  }

  function whenCartDrawerPresent() {
    if (document.getElementById('CartDrawer')) return Promise.resolve();

    return new Promise(function (resolve) {
      var settled = false;
      var finish = function () {
        if (settled) return;
        settled = true;
        resolve();
      };

      var observer = new MutationObserver(function () {
        if (document.getElementById('CartDrawer')) {
          observer.disconnect();
          finish();
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });
      document.addEventListener('DOMContentLoaded', function () {
        observer.disconnect();
        finish();
      }, { once: true });
    });
  }

  function ensureStylesheet(link) {
    var href = link.href;
    if (!href) return Promise.resolve();

    var alreadyLoaded = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(function (existingLink) {
      return existingLink.href === href;
    });
    if (alreadyLoaded) return Promise.resolve();

    return new Promise(function (resolve) {
      var stylesheet = document.createElement('link');
      Array.from(link.attributes).forEach(function (attribute) {
        stylesheet.setAttribute(attribute.name, attribute.value);
      });
      stylesheet.addEventListener('load', resolve, { once: true });
      stylesheet.addEventListener('error', resolve, { once: true });
      document.head.appendChild(stylesheet);
      window.setTimeout(resolve, 2000);
    });
  }

  function executeScript(script) {
    if (script.src) {
      var absoluteSrc = new URL(script.getAttribute('src'), window.location.href).href;
      var alreadyLoaded = Array.from(document.scripts).some(function (existingScript) {
        return existingScript.src === absoluteSrc;
      });
      if (alreadyLoaded) return Promise.resolve();

      return new Promise(function (resolve, reject) {
        var externalScript = document.createElement('script');
        Array.from(script.attributes).forEach(function (attribute) {
          externalScript.setAttribute(attribute.name, attribute.value);
        });
        externalScript.addEventListener('load', resolve, { once: true });
        externalScript.addEventListener('error', reject, { once: true });
        document.body.appendChild(externalScript);
      });
    }

    var inlineScript = document.createElement('script');
    Array.from(script.attributes).forEach(function (attribute) {
      inlineScript.setAttribute(attribute.name, attribute.value);
    });
    inlineScript.textContent = script.textContent;
    document.body.appendChild(inlineScript);
    inlineScript.remove();
    return Promise.resolve();
  }

  async function mountDrawer(stage, html) {
    var currentDrawer = document.getElementById('CartDrawer');
    if (!currentDrawer) throw new Error('Default CartDrawer mount was not found');

    var parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    var nextDrawer = parsedDocument.getElementById('CartDrawer');
    if (!nextDrawer) throw new Error('ABlyft cart response did not include #CartDrawer');
    if (nextDrawer.dataset.ablyftCartStage !== stage.replace('stage-', '')) {
      throw new Error('ABlyft cart response stage did not match its assignment');
    }

    var stylesheetPromises = Array.from(parsedDocument.querySelectorAll('link[rel="stylesheet"]')).map(ensureStylesheet);
    var scripts = Array.from(parsedDocument.querySelectorAll('script'));
    nextDrawer.querySelectorAll('script').forEach(function (script) {
      script.remove();
    });

    var wasOpen = currentDrawer.classList.contains('drawer--is-open');
    if (wasOpen) {
      nextDrawer.classList.add('drawer--is-open');
    }

    currentDrawer.replaceWith(document.importNode(nextDrawer, true));
    await Promise.all(stylesheetPromises);

    for (var script of scripts) {
      await executeScript(script);
    }

    document.documentElement.dataset.nhCartStage = stage;
    document.dispatchEvent(new CustomEvent('nh:cart-drawer-mounted', {
      detail: {
        stage: stage,
        drawer: document.getElementById('CartDrawer'),
      },
    }));

    return { stage: stage, mounted: true };
  }

  var stage = readStage();

  // The theme renders BASELINE_STAGE server-side, so it needs no swap and it is
  // what an unassigned visitor or 'control' gets. Only the later stages are fetched.
  if (!VIEWS[stage]) {
    stage = BASELINE_STAGE;
  }

  window.NH_CART_DRAWER_STAGE = stage;
  window.NH_CART_ABLYFT_ACTIVE = true;
  bindExperimentRemove();
  document.documentElement.classList.add('nh-cart-upsell-variant-c');
  document.documentElement.dataset.nhCartStage = stage;

  if (stage === BASELINE_STAGE) {
    window.NH_CART_DRAWER_READY = Promise.resolve({ stage: stage, mounted: false });
    return;
  }

  var separator = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';
  var viewUrl = separator + 'cart?view=' + VIEWS[stage] + '&t=' + Date.now();

  window.NH_CART_DRAWER_READY = Promise.all([
    fetch(viewUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    }).then(function (response) {
      if (!response.ok) throw new Error('ABlyft cart request failed with status ' + response.status);
      return response.text();
    }),
    whenCartDrawerPresent(),
  ])
    .then(function (results) {
      return mountDrawer(stage, results[0]);
    })
    .catch(function (error) {
      // The server-rendered baseline stays in place, so the cart keeps using the new item list.
      console.error('ABlyft cart treatment could not be mounted; using the baseline stage.', error);
      window.NH_CART_DRAWER_STAGE = BASELINE_STAGE;
      document.documentElement.dataset.nhCartStage = BASELINE_STAGE;
      return { stage: BASELINE_STAGE, mounted: false, error: error };
    });
})();
